/**
 * This is an extension of the default kendo Window.  It is to be used as a modal window
 * to prompt the user for information, or with a message.
 *
 * buttons - array of strings to denote what buttons should be added to the bottom of
 * the dialog.  options include: "save", "cancel" - more can be added.  "cancel" only is
 * the default, if the buttons array is not set.
 * "save"
 * "cancel"
 * "overwrite"
 * "add"
 * "update"
 *
 * Cancel button has the same affect as the window's close button.
 *
 */
import BaseWidget from "common/widgets/base/BaseWidget";
import locale from "common/managers/locale";
import topic from "common/managers/topic";
import helpManager from "common/managers/help";
import DFButton from "app/widgets/DFButton/DFButton";
import counter from "app/widgets/PopUpWindow/popupCounter";
import FocusCapture from "app/widgets/PopUpWindow/FocusCapture";
import "common/widgets/Link/Link";

const PopUpWindow = BaseWidget(kendo.ui.Window).extend({

    _backButtonRef: undefined,

    _buttons: undefined,

    /**
     * Map of all the buttons that are predefined.
     *
     * Each listing should be an object with the following properties:
     *
     * localized: The key to localize the button text.
     * id: The id to set on button.
     */
    defaultButtons: {
        refresh: {
            localized: "refresh",
            id: "refresh",
            event: "refresh"
        },
        save: {
            localized: "save",
            id: "save",
            event: "save"
        },
        overwrite: {
            localized: "overwrite",
            id: "overwrite",
            event: "overwrite"
        },
        add: {
            localized: "add",
            id: "add",
            event: "add"
        },
        choose: {
            id: "choose",
            localized: "choose",
            event: "choose"
        },
        update: {
            id: "update",
            localized: "update",
            event: "update"
        },
        "save.continue": {
            localized: "save.continue",
            id: "savecontinue",
            event: "saveContinue",
            close: true
        },
        "continue": {
            id: "continue",
            localized: "continue",
            event: "continue",
            close: true
        },
        "browse": {
            id: "browse",
            localized: "browse",
            event: "continue",
            close: true
        },
        "clearUser": {
            id: "clearUser",
            localized: "main.jobs.clearUser",
            event: "continue",
            close: true
        },
        "redistribute": {
            id: "redistribute",
            localized: "main.jobs.redistribute.action",
            event: "continue",
            close: true
        },
        "disable": {
            id: "disable",
            localized: "disable",
            event: "disable",
            close: true
        },
        "save.as": {
            localized: "save.as",
            id: "saveas",
            event: "saveAs",
            close: true
        },
        "check.in.continue": {
            localized: "check.in.continue",
            id: "checkincontinue",
            event: "checkInContinue",
            close: true
        },
        "delete": {
            id: "delete",
            localized: "delete",
            event: "delete",
            close: true
        },
        "goToLogin": {
            localized: "goToLogin",
            id: "gotologin",
            close: true
        },
        "cancel": {
            id: "cancel",
            localized: "cancel",
            event: "userClose",
            close: true
        },
        "closeLegalCase": {
            id: "closeLegalCase",
            localized: "main.rm.close.legal.case",
            event: "closeLegalCase",
            close: true
        },
        "releaseAndClose": {
            id: "releaseAndClose",
            localized: "main.rm.legalHold.release.and.close",
            event: "releaseAndClose",
            close: true
        },
        close: {
            id: "close",
            localized: "close",
            event: "userClose",
            close: true
        },
        revoke: {
            id: "revoke",
            localized: "revoke",
            event: "revokeContinue",
            close: true
        },
        clear: {
            id: "clear",
            localized: "clear",
            event: "clear",
            close: true
        },
        reset: {
            id: "reset",
            localized: "reset",
            event: "reset",
            close: true
        },
        release: {
            id: "release",
            localized: "release",
            event: "release",
            close: true
        },
        discard: {
            id: "discard",
            localized: "discard",
            event: "discard",
            close: true
        },
        logout: {
            id: "logout",
            localized: "menu.logout",
            event: "logout",
            close: true
        },
        purge: {
            id: "purge",
            localized: "purge",
            event: "purge",
            close: true
        },
        cancelEdit: {
            id: "cancelEdit",
            localized: "admin.editing.cancel",
            event: "cancelEdit",
            close: true
        },
        fax: {
            id: "fax",
            localized: "main.fax.button",
            event: "fax",
            close: false
        },
        email: {
            id: "email",
            localized: "email",
            event: "email",
            close: false
        },
        export: {
            id: "export",
            localized: "export",
            event: "export",
            close: false
        },
        replace: {
            id: "replace",
            localized: "replace",
            event: "replace",
            close: false
        },
        download: {
            id: "download",
            localized: "download",
            event: "download",
            close: false
        },
        start: {
            id: "start",
            localized: "start",
            event: "start",
            close: true
        },
        "setAsDefault": {
            id: "setAsDefault",
            localized: "setAsDefault.button",
            event: "save"
        }
    },

    options: {

        name: "PopUpWindow",

        // hide the default close button and provide our own
        actions: ["customclose"],

        // button options include:
        // "cancel", "close", "discard", "reset",
        // "refresh", "save", "overwrite", "choose", "add", "update",
        // "save.continue", "save.as", "check.in.continue",
        // "delete",
        // "goToLogin",
        // "continue",
        // "disable"
        buttons: ["cancel"],

        primaryButton: null,

        //The key name of the localized string to use as the title.  This overrides the title property if defined
        localizedTitle: false,

        title: "",

        //When set to true, the primary action button will be focused and the enter key will trigger the primary button.
        simpleDialog: false,

        // whether to hook up to the enter key.  Can be true to trigger the default button, or the name of an event.
        enterKey: "",

        windowClass: "",

        helpPage: "",

        resizable: false,

        visible: false,

        modal: true,

        draggable: false,

        //Append buttons
        appendButtons: false,

        // disable auto-focus for custom data-focus behavior
        autoFocus: false,

        // whether pause disables buttons in impl content or not, default is true (pause is called from linkRequest)
        // it can be helpful to turn this off when using validation on a widget like FileInput because kendo button calls $().blur() in the enable function
        pauseContentButtons: true
    },

    _templates: {
        bottomDiv: '<div class="dialogBottom"></div>',
        listParent: '<ul class="dialogActions flexRow dialogButtons" role="presentation"></ul>',
        helpParent: '<ul class="dialogActions dialogHelp" role="presentation"><li><a data-role="link" tabindex="0" class="help iconMoon">H</a></li></ul>'

    },

    init: function(element, options) {
        var me = this;

        if (options.localizedTitle) {
            options.title = locale.get(options.localizedTitle);
        }

        if (!options.appendTo) {
            options.appendTo = document.body;
        }

        this._counterGuid = kendo.guid();
        kendo.ui.Window.fn.init.call(this, element, options);
        this.element.removeAttr("tabindex");
        this._titleAria();
        this._captureFocus = new FocusCapture();
        this._buttons = {};
        $(window).on(`resize.${this._counterGuid}`, this.center.bind(this));

        if (this.element.attr("id") === undefined) {
            console.error("PopUpWindow Widget widgets require an id");
        } else {
            var id = this.element.attr("id");
        }

        this.options.buttons = this.options.buttons
            .map(function(button) {
                if (me.defaultButtons.hasOwnProperty(button)) {
                    return me.defaultButtons[button];
                } else {
                    return button;
                }
            });


        const bottomId = `${id}_bottom`;
        this.bottomDiv = $(this._templates.bottomDiv).attr("id", bottomId);

        // Add buttons based on options in options.buttons
        var list = $(this._templates.listParent);
        this._setButtonStyles(list);
        this.bottomDiv.append(list);

        // show a help button if the options contains a help page
        if (options.helpPage) {
            var helpParent = $(this._templates.helpParent),
                help = helpParent.find("a");
            help.attr("title", locale.get("help"))
                .attr("alt", locale.get("help"))
                .attr("tabindex", 0)
                .attr("id", id + "_btn_help");
            this.bottomDiv.append(helpParent);
        }

        var multiple = me.options.buttons.length > 1;
        this.options.buttons.forEach(function(button) {
            var li = $("<li>");
            var btn = $("<button>")
                .attr("id", id + "_btn_" + button.id)
                .addClass("k-button")
                .text(locale.get(button.localized, button.extra));

            if (me.options.primaryButton && button.id === me.options.primaryButton) {
                btn.addClass("primary");
            } else {
                if (!me.options.primaryButton && button.id === me.options.buttons[0].id) {
                    btn.addClass("primary");
                } else {
                    btn.addClass(multiple ? "secondary" : "cancel");
                }
            }

            li.append(btn);
            list.append(li);
        });

        if (this.options.appendButtons) {
            this.element.append(me.bottomDiv);
        } else {
            me.content(me.content() + me.bottomDiv[0].outerHTML);
            this.bottomDiv = me.wrapper.find(`#${bottomId}`);
        }

        me.one("open", function() {
            // add a class if provided in the options
            if (me.options.windowClass) {
                me.element.parent().addClass(me.options.windowClass);
                me.minWidth = undefined;
            }

            if (me.options.modal) {
                me._captureFocus.capture();
            }
        });
        me.one("activate", () => {
            this._setupCustomClose();
            this._resizeChildren();
            if (me.options.helpPage !== false) {
                $("#" + id + "_btn_help").click(function() {
                    helpManager.open(me.options.helpPage);
                });
            }

            this._buttons = {};
            me.options.buttons.forEach((button) => {
                let btn = $("#" + id + "_btn_" + button.id);
                this._buttons[button.id] = new DFButton(btn, {
                    click: () => {
                        this._buttonClicked = button.id;
                        if (button.event) {
                            this.trigger("action", {
                                event: button.event
                            });
                            this.trigger(button.event);
                        }
                        if (button.close) {
                            this.close();
                        }
                    }
                });
                if(btn.hasClass("primary")) {
                    this._primaryButton = button.id;
                }
            });

            if (options.enterKey) {
                me.listenForEnterKey(options.enterKey !== true ? options.enterKey : undefined);
            }

            //Kendos tabkey trap is not accurate enough to be used
            if (me._tabKeyTrap) {
                me._tabKeyTrap.removeTrap();
            }
        });

        this._buildTabTrap();

        me.bind("activate", function() {
            // listen for the back button and prevent it while this popup is open.
            // only applicable for dashboards designer, should port to use onHide when refactored
            me._backButtonRef = topic.subscribe("nav/dashboardsDesigner/back", $.proxy(me.handleBackButton, me));
            me.one("close", function() {
                topic.unsubscribe(me._backButtonRef);
            });

            // focus upon activate when all transitions are done
            var focusEls = me.wrapper.find("[data-focus]");
            let focusable;
            if (focusEls.length > 0) {
                // look for widget first, then element
                var focusEl = focusEls.first();
                var w = kendo.widgetInstance(focusEl);
                if (w && w.focus) {
                    if((w.wrapper || w.element).is(":visible")) {
                        focusable = w;
                    }
                } else if(focusEl.is(":visible")) {
                    focusable = focusEl;
                }
            }
            // default to window element
            (focusable || me.wrapper).focus();
        });

        //Use addEventListener so that capture can be used.  This ensure that nothing stops the key up from being fired
        this.wrapper[0].addEventListener("keydown", $.proxy(this._handleKeyDown, this), true);

        if (this.options.simpleDialog) {
            this._makeSimpleDialog();
        }
    },

    _buildTabTrap() {
        //Kendo has a built-in tab trap, but it is not compatible with PromptList.
        //For the same reason, it is not possible in general to inspect tabbable elements inside the content of a popup as they change constantly
        //This works by treating the Window itself as the shift+tab stop and the bottom actions(buttons or help link) as the tab stop.
        if(!this.options.modal) {
            return;
        }
        this.bottomDiv.on("keydown.PopUpWindow", (e) => {
            if(e.keyCode === kendo.keys.TAB && !e.shiftKey) {
                //This is not all possible tabbable elements, but it does account for the ones that exist in bottomDiv
                const tabbable = this.bottomDiv.find("a[tabindex=0]:visible, button:not(disabled):visible");
                const now = e.target;
                const index = tabbable.index(now);
                if(index === tabbable.length - 1) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        });
        //It's necessary to do the jquery on() like this because Kendo is screwing with the on function for wrapper
        $(this.wrapper[0]).on("keydown.PopUpWindow", (e) => {
            if(e.keyCode === kendo.keys.TAB && !!e.shiftKey && e.target === this.wrapper[0]) {
                e.preventDefault();
                e.stopPropagation();
            }
        });
    },

    _setupCustomClose: function() {
        //There is no easy way to add a tooltip to just this windows close button
        var c = locale.get("close");

        // hook up to the custom close button
        var close = this.wrapper.find("button.k-window-titlebar-action:last-child>.k-icon");
        close
            .text("n")
            .parent()
            .attr("title", c)
            .attr("alt", c)
            .attr("aria-label", c);

        var me = this;
        close.closest("button")
            .off("click.customclose")
            .on("click.customclose", function(e) {
                e.preventDefault();
                e.stopPropagation();
                me.onHide();
            });
    },

    /**
     * Kendo's base widget does this but it doesn't account for our extended widgets
     */
    _resizeChildren() {
        const roles = ["dfslider"];
        const widgets = this.element.children().find(roles.map((role) => `[data-role=${role}]`).join()).toArray();
        widgets.forEach((widget) => {
            kendo.widgetInstance($(widget))?.resize();
        });
    },

    _removeOverlay: function() {
        this._captureFocus.release();
        return kendo.ui.Window.fn._removeOverlay.apply(this, arguments);
    },

    _titleAria: function() {
        this.wrapper.find(".k-window-title").attr("role", "heading").attr("aria-level", "1");
    },

    setOptions: function(options) {

        if (options.windowClass) {
            this.options.windowClass = options.windowClass;
        }

        if (options.hasOwnProperty("title")) {
            this.wrapper.find(".k-window-title").text(options.title);
        } else if (options.helpPage) {
            this.options.helpPage = options.helpPage;
        } else {
            kendo.ui.Window.fn.setOptions.apply(this, arguments);
            this._setupCustomClose();
        }
    },

    onHide: function() {
        // Trigger a userClose event.  Extensions can use preventDefault to stop the window from closing.
        if (!this.trigger("userClose") && this.wrapper.is(":visible")) {
            this.close();
        }
    },

    _handleKeyDown: function(e) {
        if (e && e.keyCode === 27) {
            //Handle esc key.  Delegate to standard close behavior.
            this.onHide();
        } else if(e && e.keyCode === 13 && this.options.simpleDialog && !$(e.target).is("button")) {
            this.primary().click();
        }
    },

    /**
     * Fail the given deferred if the user closes the window.
     * @param deferred
     */
    failOnClose: function(deferred) {
        var bind = function(e) {
            setTimeout(function() {
                if (!e.isDefaultPrevented() && e.userTriggered !== false) {
                    deferred.reject();
                }
            });
        };
        this.bind("close", bind);
        this.bind("userClose", bind);
        var me = this;
        deferred.always(function() {
            me.unbind("close", bind);
            me.unbind("userClose", bind);
        })
    },

    /**
     * Resolve the given deferred if the user clicks a non-close button
     * @param deferred
     */
    resolveOnAction: function(deferred) {
        this.options.buttons
            .map(function(b) {
                return b.event;
            })
            .filter(function(event) {
                return !!event && event !== "cancel" && event !== "close" && event !== "userClose";
            })
            .forEach((event) => {
                this.one(event, (e) => {
                    deferred.resolve(e);
                })
            });
    },

    _stopClose: function(e) {
        e.preventDefault();
    },

    /**
     * Show/hide spinner and disable/enable interaction
     * @param pause
     */
    pause: function(pause) {
        if (pause) {
            this.bind("close", this._stopClose);
        } else {
            this.unbind("close", this._stopClose);
        }
        this.wrapper.find("input,textarea,select").add(this._getButtonsToDisable()).each(function(i, e) {
            var el = $(e);
            var w = kendo.widgetInstance(el);
            if (w && w.enable) {
                w.enable(!pause);
            }
            el.attr("disabled", !!pause);
            el.toggleClass("disabled", !!pause);
        });
    },

    _getButtonsToDisable() {
        // don't disable the action button
        const clicked = this._buttons ? this._buttons[this._buttonClicked]?.element.attr("id") : undefined;
        let selector = "";
        if(clicked) {
            selector = `:not(#${clicked})`;
        }
        let buttons = this.wrapper.find(`button${selector}`);
        if(!this.options.pauseContentButtons) {
            buttons = $();
            Object.values(this._buttons).forEach((button) => {
                const element = button.element;
                if(!clicked || element.attr("id") !== clicked) {
                    buttons = buttons.add(element);
                }
            });
        }
        return buttons;
    },

    /**
     * Link appropriate pause() method calls to a given promise
     * @param promise
     */
    linkRequest: function(promise) {
        if(this._destroyed) {
            return;
        }
        const btn = this._buttons[this._buttonClicked || this._primaryButton];
        if(btn) {
            btn.value(promise);
        }
        this.pause(true);
        return promise.always(() => {
            this.pause(false);
        });
    },

    clickedHasFocus() {
        const clicked = this._buttons ? this._buttons[this._buttonClicked]?.element[0] : undefined;
        return document.activeElement === clicked;
    },

    /**
     * Link pause() method calls to multiple promises.
     *
     * @param promises array of promises
     */
    linkRequests: function(promises) {
        this.linkRequest($.when.apply($, promises));
    },

    /**
     * Toggle one of the initialized buttons
     * @param button button to toggle
     * @param showOrHide true to show, false to hide
     * @param btnToPrimary button to toggle to primary, if any
     */
    toggleButton: function(button, showOrHide, btnToPrimary) {
        const me = this;
        $("#" + me.element.attr("id") + "_btn_" + button).toggle(showOrHide);
        if (btnToPrimary) {
            this.togglePrimaryButton(btnToPrimary, true).toggle(true);
        }
        return this;
    },

    /**
     * Toggle which button is marked as the primary button.
     * @param id Button id
     * @param isPrimary {boolean} Is this button the primary.
     */
    togglePrimaryButton: function(id, isPrimary) {
        if (isPrimary) {
            this.wrapper.find("button.primary[id^='" + this.element.attr("id") + "_btn_']")
                .removeClass("primary")
                .addClass("secondary");
        }
        if(isPrimary && this.wrapper.find("#" + this.element.attr("id") + "_btn_" + id)) {
            this._primaryButton = id;
        } else {
            this._primaryButton = undefined;
        }
        return this.wrapper
            .find("#" + this.element.attr("id") + "_btn_" + id)
            .toggleClass("primary", isPrimary)
            .toggleClass("secondary", !isPrimary);
    },

    open: function() {
        this.trigger("readyToOpen");
        $.when(this._stallFor).always(() => {
            if (!this._destroyed) {
                kendo.ui.Window.fn.open.call(this);
                // turn off focus hijacking in kendo so window focus focuses the correct dom object
                $(window).off("focus.kendoWindowModal");
                kendo.ui.Window.fn.center.call(this);
                counter.add(this._counterGuid);
            }
        });
    },

    isOpen: function() {
        return !!this.options.visible;
    },

    /**
     * This promise will be failed if cancel or close is hit.  It will be resolved if an action button is pressed.
     */
    promise: function() {
        var def = $.Deferred();
        this.failOnClose(def);
        this.resolveOnAction(def);
        return def.promise();
    },

    /**
     * Stall calls to open until the promise is fulfilled.  Only supports one promise at a time
     * @param promise
     */
    stallOpen: function(promise) {
        this._stallFor = promise;
    },

    close: function() {
        topic.unsubscribe(this._backButtonRef);
        kendo.ui.Window.fn.close.call(this);
        counter.remove(this._counterGuid);
    },

    destroy: function() {
        for(var item in this._buttons) {
            this._buttons[item].destroy();
        }
        delete this._buttons;
        delete this._primaryButton;
        delete this._buttonClicked;
        this._destroyed = true;
        this.wrapper.off(".PopUpWindow");
        $(window).off(`.${this._counterGuid}`);
        kendo.ui.Window.fn.destroy.call(this);
        counter.remove(this._counterGuid);
        if(this.bottomDiv) {
            this.bottomDiv.off(".PopUpWindow")
            delete this.bottomDiv;
        }
    },

    handleBackButton: function(e) {
        this.onHide();
        e.preventDefault();
    },

    listenForEnterKey: function(eventName) {
        const onKeyPress = (e) => {
                if ((e.which && e.which === 13) || (e.keyCode && e.keyCode === 13)) {

                    // fire a blur to trigger kendo to validate.
                    $(e.target).trigger("blur");

                    // keep the focus after blur
                    $(e.target).focus();

                    // fire an event that implementors can hook up to and do what they wish.
                    if (eventName) {
                        this.trigger(eventName);
                    } else {
                        this.primary().click();
                    }
                    return false;
                } else {
                    return true;
                }
            };
        this.element.find("form input").keypress(onKeyPress);
        this.element.find("div.form input").keypress(onKeyPress);
    },

    primary: function() {
        return this._buttons[this._primaryButton].element;
    },

    /**
     * Should be called on simple dialogs before they are opened.
     * This will set the primary button as the focus target and makes the enter button trigger the primary action.
     * @private
     */
    _makeSimpleDialog: function() {
        this.bind("activate", () => {
            this.primary().focus();
        });
    },

    /**
     * Set the flex classes to layout the buttons in a popupwindow
     * @private
     */
    _setButtonStyles: function() {
        if (this.options.helpPage) {
            this.bottomDiv.addClass("flexSplit");
        } else {
            this.bottomDiv.addClass("flexRight");
        }
    }
});

kendo.ui.plugin(PopUpWindow);
export default PopUpWindow;
